import React from "react";
import { Box, Typography, Divider, Grid, Paper } from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

const COLORS = [
  "#0088FE", "#FF8042", "#00C49F", "#FFBB28",
  "#AA66CC", "#FF6666", "#FFA07A", "#20B2AA",
];

const calculateAverageResolutionTime = (workOrders) => {
  const resolved = workOrders.filter(w => w.resolved_at && w.created_at);
  if (resolved.length === 0) return 0;

  const totalMs = resolved.reduce((acc, w) => {
    const created = new Date(w.created_at);
    const resolvedDate = new Date(w.resolved_at);
    return acc + (resolvedDate - created);
  }, 0);

  const avgMs = totalMs / resolved.length;
  const avgDays = avgMs / (1000 * 60 * 60 * 24);
  return avgDays.toFixed(1);
};

const MemberAnalyticsPanel = ({ workOrders }) => {
  const totalOrders = workOrders.length;
  const openOrders = workOrders.filter(w => !w.resolved_at).length;
  const avgResolveTime = calculateAverageResolutionTime(workOrders);

  // Pie data by subtype
const subtypeData = Object.entries(
  workOrders.reduce((acc, w) => {
    const subtype = w.subtype_name || "Unspecified";
    acc[subtype] = (acc[subtype] || 0) + 1;
    return acc;
  }, {})
)
  .map(([name, value]) => ({ name, value }))
  .sort((a, b) => b.value - a.value) 
  .slice(0, 5); 



  return (
    <Box mt={4}>
      <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
        Work Order Analytics
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {/* KPI Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Total Work Orders
            </Typography>
            <Typography variant="h4" fontWeight="bold">
              {totalOrders}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Open Work Orders
            </Typography>
            <Typography variant="h4" fontWeight="bold">
              {openOrders}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Avg. Resolution Time (days)
            </Typography>
            <Typography variant="h4" fontWeight="bold">
              {avgResolveTime}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Pie Chart: Orders by Subtype */}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" mb={2}>
              Work Orders by Subtype
            </Typography>
            {subtypeData.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No data available to display.
              </Typography>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={subtypeData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={110}
                    paddingAngle={6}
                    label
                  >
                    {subtypeData.map((_, index) => (
                      <Cell key={index} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, name) => [`${value}`, `${name}`]}
                    contentStyle={{ fontSize: "0.85rem" }}
                  />
                  <Legend verticalAlign="bottom" height={36} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MemberAnalyticsPanel;
